import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save,
  RefreshCw,
  X,
  CalendarDays
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../../api/axios';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../../context/LoadingContext';
import styles from './PayrollForm.module.css';

import CustomSelect from '../../../components/Common/Select/CustomSelect';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 8 }, (_, i) => {
  const y = currentYear + 1 - i;
  return { value: y, label: String(y) };
});
const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const BulkGeneratePayroll = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    showLoader();
    try {
      const response = await api.post('/payroll/bulk-generate', formData);
      if (response.data.success) {
        toast.success(response.data.message || 'Bulk payroll generation started successfully');
        navigate('/employees/payroll');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start bulk generation');
    } finally {
      setIsGenerating(false);
      hideLoader();
    }
  };

  return (
    <div className={`${styles['create-po-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        
        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs customItems={[
                { label: 'HR', path: '#' },
                { label: 'Payroll / Salary', path: '/employees/payroll' },
                { label: 'Bulk Generate', path: '#' }
              ]} />
              <h1>Bulk Generate Payroll</h1>
              <p>Schedule payroll calculation for all active employees</p>
            </div>
            
            <div className={styles['header-actions-btns']}>
              <button type="button" onClick={() => navigate('/employees/payroll')} className={styles['btn-cancel']}>
                <X size={18} /> <span>Cancel</span>
              </button>
              <button type="button" onClick={handleGenerate} className={styles['btn-primary']} disabled={isGenerating}>
                {isGenerating ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
                <span>Start Bulk Generation</span>
              </button>
            </div>
          </div>

          <div className={styles['form-sections-grid']} style={{ gridTemplateColumns: '1fr' }}>
            
            <div className={styles['form-card']} style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
              <div className={styles['card-header-po']}>
                <h3>Payroll Configuration</h3>
              </div>
              <div className={styles['card-content']}>
                
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginBottom: '24px', display: 'flex', gap: '12px' }}>
                  <CalendarDays style={{ color: '#2563eb', flexShrink: 0 }} size={24} />
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', color: '#1e3a8a', fontSize: '15px' }}>Background Processing</h4>
                    <p style={{ margin: 0, color: '#1e40af', fontSize: '14px' }}>
                      Selecting a month and year will queue a background task. The system will process each active employee individually, factoring in their attendance, loans, advances, and paid holidays.
                    </p>
                  </div>
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Select Month <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={months.find(m => m.value === Number(formData.month)) || null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, month: opt ? opt.value : '' }))}
                      options={months}
                      placeholder="Select Month"
                      isSearchable={false}
                    />
                  </div>
                  
                  <div className={styles['form-group']}>
                    <label>Select Year <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={yearOptions.find(y => y.value === Number(formData.year)) || null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, year: opt ? opt.value : '' }))}
                      options={yearOptions}
                      placeholder="Select Year"
                      isSearchable={false}
                    />
                  </div>
                </div>

              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default BulkGeneratePayroll;
